// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { CreateAdvisoryAccountsCitecRequestBody } from '@shared/data-access/types/brokerage-types';
import type { BankUploadForm } from '@client/banks/types/create-bank-form';
import { toFormOptionalIban } from '@client/banks/utils/account-reference';
import { BankAccountType } from '@client/banks/types/account';
import {
  type BankDebtAmortizationStatus,
  BankDebtProductType,
} from '@client/banks/types/debt';
import { BankPositionAssetType } from '@client/banks/types/position';
import type {
  BankTransactionSubtype,
  BankTransactionType,
} from '@client/banks/types/transaction';

/**
 * Maps bulk upload request body data to {@link BankUploadForm}.
 *
 * @remarks
 * Inverse of `buildBulkUploadAccountData`. Wire rows map 1:1 back to camelCase form
 * rows; owner `role` is dropped because the form only collects names.
 * @param payload - `CreateAdvisoryAccountsCitecRequestBody` from the API or Redux
 *   draft.
 */
export const buildBankUploadFormFromAccountData = (
  payload: CreateAdvisoryAccountsCitecRequestBody,
): BankUploadForm => ({
  institution: {
    name: payload.institution?.name ?? '',
    logoUrl: (payload.institution?.logo_url ?? '').trim(),
  },
  accounts: (payload.accounts ?? []).map(account => ({
    iban: toFormOptionalIban(account.iban),
    name: account.name,
    currency: account.currency,
    accountType:
      (account.account_type as BankAccountType) ?? BankAccountType.Checking,
    available: account.available ?? null,
    balanceDate: account.balance_date || null,
    owners: (account.owners ?? [])
      .map(owner => owner.name?.trim() ?? '')
      .filter(Boolean)
      .map(name => ({ name })),
  })),
  positions: (payload.positions ?? []).map(position => ({
    iban: toFormOptionalIban(position.iban),
    isin: position.isin ?? '',
    name: position.name ?? '',
    currency: position.currency,
    assetType:
      (position.asset_type as BankPositionAssetType) ?? BankPositionAssetType.Other,
    acquisitionCost: position.acquisition_cost ?? undefined,
    quantity: position.quantity,
    price: position.price,
    snapshotDate: position.snapshot_date,
  })),
  transactions: (payload.transactions ?? []).map(transaction => ({
    iban: toFormOptionalIban(transaction.iban),
    transactionDate: transaction.transaction_date,
    amount: transaction.amount,
    currency: transaction.currency,
    description: transaction.description ?? '',
    name: transaction.name ?? undefined,
    isin: transaction.isin ?? undefined,
    quantity: transaction.quantity ?? undefined,
    price: transaction.price ?? undefined,
    type: (transaction.type as BankTransactionType) ?? undefined,
    assetType: (transaction.asset_type as BankPositionAssetType) ?? undefined,
    subtype: (transaction.subtype as BankTransactionSubtype) ?? undefined,
  })),
  debts: (payload.debts ?? []).map(debt => ({
    iban: toFormOptionalIban(debt.iban),
    productType:
      (debt.product_type as BankDebtProductType) ?? BankDebtProductType.Loan,
    name: debt.name,
    currency: debt.currency,
    pendingAmount: debt.pending_amount,
    snapshotDate: debt.snapshot_date,
    contractNumber: debt.contract_number ?? undefined,
    grantedCapital: debt.granted_capital ?? undefined,
    interestRate: debt.interest_rate ?? undefined,
    limitAmount: debt.limit_amount ?? undefined,
    constitutionDate: debt.constitution_date ?? undefined,
    expirationDate: debt.expiration_date ?? undefined,
    nextInstallment: debt.next_installment ?? undefined,
    pendingReceipts: debt.pending_receipts ?? undefined,
    available: debt.available ?? undefined,
    disposed: debt.disposed ?? undefined,
    currentBalance: debt.current_balance ?? undefined,
    nextPaymentDate: debt.next_payment_date ?? undefined,
    amortizations: (debt.amortizations ?? []).map(amortization => ({
      installmentDate: amortization.installment_date,
      status: amortization.status as BankDebtAmortizationStatus | undefined,
      amortized: amortization.amortized ?? undefined,
      interest: amortization.interest ?? undefined,
      total: amortization.total ?? undefined,
      pendingCapital: amortization.pending_capital ?? undefined,
    })),
  })),
});
